import {
  ClickHeuristic,
  findAncestor,
  getDisplayValue,
  parentWithAttribute,
  parentWithClass,
} from '@cast/analytics';

import { resolveContextualData } from './_resolveContextualData';
import {
  getSysAttrs,
  getSysId,
  isCheckboxChecked,
  parentWithSysId,
  resolveControlEvent,
} from './_utils';

const withContext = (
  el: Element,
  title: string,
  attrs: Record<string, unknown>
) => ({
  title,
  type: 'click',
  ...attrs,
  ...resolveContextualData(el),
});

const buttonClick: ClickHeuristic = (el) => {
  const buttonEl = findAncestor({
    element: el,
    predicate: (el) =>
      el.tagName === 'BUTTON' ||
      el.getAttribute('role') === 'button' ||
      el.classList.contains('MuiButtonBase-root'),
  });
  if (!buttonEl) {
    return;
  }
  const buttonType = (() => {
    if (buttonEl.classList.contains('DsIconButton-root')) {
      return 'icon-button';
    }
    if (buttonEl.classList.contains('MuiToggleButton-root')) {
      return 'toggle-button';
    }
    return undefined;
  })();

  return withContext(el, 'Button Clicked', {
    ...getSysAttrs(buttonEl),
    displayValue: getDisplayValue(buttonEl),
    buttonType,
  });
};

const linkClick: ClickHeuristic = (el) => {
  const linkEl = parentWithAttribute({ attr: 'href', element: el });
  if (!linkEl) {
    return;
  }
  const url = linkEl.getAttribute('href');
  // Omit virtual clicks to download content
  if (!url || url.startsWith('blob:')) {
    return;
  }
  return withContext(el, 'Link Clicked', {
    ...getSysAttrs(linkEl),
    displayValue: getDisplayValue(linkEl),
    link: url,
    isExternalLink: url.startsWith('http'),
  });
};

const entityActionsClick: ClickHeuristic = (el) => {
  const entityActionsTrigger = parentWithClass({
    className: 'EntityActions-triggerButton',
    element: el,
  });
  if (!entityActionsTrigger) {
    return;
  }
  const isTableRowActions = !!parentWithClass({
    className: 'DS-Table-ActionIcon',
    element: entityActionsTrigger,
  });
  return withContext(
    el,
    isTableRowActions ? 'Row Actions Opened' : 'Popup Menu Opened',
    {
      ...getSysAttrs(el),
      isTableRowActions,
    }
  );
};

const closeDrawerButtonClick: ClickHeuristic = (el) => {
  const found = parentWithSysId({ sysId: 'close-drawer', element: el });
  if (!found) {
    return;
  }
  return withContext(el, 'Close Drawer Button Clicked', {
    ...getSysAttrs(found),
    displayValue: getDisplayValue(found),
  });
};

const tableSettingsButtonClick: ClickHeuristic = (el) => {
  const found = parentWithSysId({
    sysId: 'table-settings-button',
    element: el,
  });
  if (!found) {
    return;
  }
  return withContext(el, 'Table Settings Button Clicked', {
    ...getSysAttrs(found),
    displayValue: getDisplayValue(found),
  });
};

const tableColumnSortClick: ClickHeuristic = (el) => {
  const found = parentWithClass({
    className: 'Ds-Table-SortIcon-root',
    element: el,
  });
  if (!found) {
    return;
  }
  return withContext(el, 'Table Column Sorted', {
    ...getSysAttrs(found),
    displayValue: getDisplayValue(found),
  });
};

const radioButtonClick: ClickHeuristic = (el) => {
  const { event } = resolveControlEvent(el, 'MuiRadio-root', 'radio');
  if (!event) {
    return;
  }
  return withContext(el, 'Radio Button Clicked', event);
};

const switchButtonClick: ClickHeuristic = (el) => {
  const { event, targetEl } = resolveControlEvent(
    el,
    'DS-Switch-root',
    'switch'
  );
  if (!event || !targetEl) {
    return;
  }
  const input = targetEl.querySelector<HTMLInputElement>('input');
  return withContext(el, 'Switch Clicked', {
    ...event,
    isChecked: input?.checked,
  });
};

const checkboxClick: ClickHeuristic = (el) => {
  const { event, targetEl } = resolveControlEvent(
    el,
    'MuiCheckbox-root',
    'checkbox'
  );

  if (!event || !targetEl) {
    return;
  }

  const checkboxType = (() => {
    const cell = parentWithClass({
      className: 'DS-Table-Cell',
      element: targetEl,
    });
    if (cell) {
      return cell.classList.contains('DS-Table-HeaderCell')
        ? 'table-header'
        : 'table-row';
    }
    return parentWithClass({ className: 'DS-Option', element: targetEl })
      ? 'dropdown'
      : undefined;
  })();

  const title = (() => {
    switch (checkboxType) {
      case 'table-header':
        return 'Table Header Checkbox Clicked';
      case 'table-row':
        return 'Table Row Checkbox Clicked';
      case 'dropdown':
        return 'Dropdown Checkbox Clicked';
      default:
        return 'Checkbox Clicked';
    }
  })();

  return withContext(el, title, {
    ...event,
    checkboxType,
    isChecked: isCheckboxChecked(targetEl),
  });
};

const dropdownInputClick: ClickHeuristic = (el) => {
  const dropdownRoot = parentWithClass({
    className: 'DS-Dropdown-root',
    element: el,
    maxLevelUp: 7,
  });
  if (!dropdownRoot) {
    return;
  }
  const multiValueText = getDisplayValue(
    dropdownRoot.querySelector<HTMLElement>('.DS-Dropdown-multiValueText')
  );
  const label = getDisplayValue(
    dropdownRoot.querySelector<HTMLElement>('.DS-InputLabel-label')
  );
  const inputValue = dropdownRoot.querySelector('input')?.value;

  return withContext(el, 'Dropdown Input Clicked', {
    ...getSysAttrs(dropdownRoot),
    displayValue: label || multiValueText || inputValue,
    label,
    inputText: multiValueText,
    inputValue,
  });
};

const alertActionClick: ClickHeuristic = (el) => {
  const alertActionEl = parentWithClass({
    className: 'MuiAlert-action',
    element: el,
  });
  if (!alertActionEl) {
    return;
  }
  const isCloseAlert =
    !!alertActionEl.querySelector<HTMLElement>('.DsAlert-close');
  return withContext(
    el,
    isCloseAlert ? 'Alert Closed' : 'Alert Action Clicked',
    {
      ...getSysAttrs(alertActionEl),
      isCloseAlert: isCloseAlert,
    }
  );
};

const dropdownOptionClick: ClickHeuristic = (el) => {
  const dropdownListItem = parentWithSysId({
    sysId: /^dropdown-list/,
    maxLevelUp: 8,
    element: el,
  });
  if (!dropdownListItem) {
    return;
  }
  const sysId = getSysId(dropdownListItem);
  const optionType = (() => {
    if (sysId === 'dropdown-list-select-all') {
      return 'select-all';
    }
    if (sysId?.startsWith('dropdown-list-select-group-')) {
      return 'select-group';
    }
    return undefined;
  })();

  const title = (() => {
    switch (optionType) {
      case 'select-all':
        return 'Select All Dropdown Options Clicked';
      case 'select-group':
        return 'Dropdown Option Group Clicked';
      default:
        return 'Dropdown Option Clicked';
    }
  })();

  const checkboxEl =
    dropdownListItem.querySelector<HTMLElement>('.MuiCheckbox-root');
  return withContext(el, title, {
    ...getSysAttrs(dropdownListItem),
    displayValue: getDisplayValue(dropdownListItem),
    optionType,
    isOptionSelected: checkboxEl ? isCheckboxChecked(checkboxEl) : undefined,
  });
};

const searchCriterionChipClick: ClickHeuristic = (el) => {
  const chip = parentWithSysId({
    sysId: /(search|selected)-criteria-chip/,
    element: el,
  });
  if (!chip) {
    return;
  }
  const triggerSysId = getSysId(el);
  const criterionCleared = triggerSysId === 'destroy-chip';
  return withContext(
    el,
    criterionCleared
      ? 'Search Criterion Cleared'
      : 'Search Criterion Chip Clicked',
    {
      ...getSysAttrs(el),
      displayValue: getDisplayValue(chip),
      criterionCleared,
    }
  );
};

const searchDropdownOptionClick: ClickHeuristic = (el) => {
  const option = parentWithClass({
    className: 'DsSearch-OptionsListItem',
    element: el,
  });
  if (!option) {
    return;
  }
  const checkboxEl = option.querySelector<HTMLElement>('.MuiCheckbox-root');
  return withContext(el, 'Search Dropdown Option Clicked', {
    ...getSysAttrs(el),
    displayValue: getDisplayValue(option),
    isOptionSelected: checkboxEl ? isCheckboxChecked(checkboxEl) : undefined,
  });
};

const clearAllFiltersClick: ClickHeuristic = (el) => {
  const chipInputEndIcon = parentWithClass({
    className: 'DsChipInput-EndIcon',
    element: el,
  });
  if (chipInputEndIcon) {
    const searchBox = parentWithClass({
      className: 'DsSearch-Root',
      element: el,
      maxLevelUp: 8,
    });
    const clearAllType = searchBox
      ? 'clear-all-search-box-filters'
      : 'clear-all-chips';
    const title =
      clearAllType === 'clear-all-search-box-filters'
        ? 'Clear Search Box Filters Clicked'
        : 'Clear All Chips Clicked';
    return withContext(el, title, {
      ...getSysAttrs(chipInputEndIcon),
      clearAllType,
    });
  }
  if (el.getAttribute('aria-label') === 'clear all filters') {
    return withContext(el, 'Clear All Filters Clicked', {
      ...getSysAttrs(el),
      displayValue: getDisplayValue(el),
      clearAllType: 'clear-all-filters',
    });
  }
  return undefined;
};

const sidebarMenuItemClick: ClickHeuristic = (el) => {
  const found = findAncestor({
    element: el,
    predicate: (el) => !!getSysId(el)?.startsWith('sidebar-item-'),
    maxLevelUp: 8,
  });
  if (!found) {
    return;
  }
  return withContext(el, 'Sidebar Menu Item Clicked', {
    ...getSysAttrs(found),
    displayValue: getDisplayValue(found),
  });
};

const topNavMenuItemClick: ClickHeuristic = (el) => {
  const found = parentWithClass({
    className: 'TopNavHeaderButton-root',
    maxLevelUp: 3,
    element: el,
  });
  if (!found) {
    return;
  }
  return withContext(el, 'Top Navigation Menu Item Clicked', {
    ...getSysAttrs(found),
    displayValue: getDisplayValue(found),
  });
};

const copyToClipboardClick: ClickHeuristic = (el) => {
  const found = parentWithClass({
    className: 'CopyToClipboard-button',
    maxLevelUp: 2,
    element: el,
  });
  if (!found) {
    return;
  }
  return withContext(el, 'Copy to Clipboard Clicked', {
    ...getSysAttrs(found),
    displayValue: getDisplayValue(found),
  });
};

const tabClick: ClickHeuristic = (el) => {
  const found = parentWithClass({
    className: 'MuiTab-root',
    maxLevelUp: 3,
    element: el,
  });
  if (!found) {
    return;
  }
  return withContext(el, 'Tab Selected', {
    ...getSysAttrs(found),
    displayValue: getDisplayValue(found),
  });
};

/**
 * The order in which click heuristics are matched. More specific matches should come first.
 */
export const clickHeuristics: ClickHeuristic[] = [
  copyToClipboardClick,
  closeDrawerButtonClick,
  tableSettingsButtonClick,
  tableColumnSortClick,
  alertActionClick,
  entityActionsClick,
  searchCriterionChipClick,
  searchDropdownOptionClick,
  dropdownInputClick,
  dropdownOptionClick,
  topNavMenuItemClick,
  sidebarMenuItemClick,
  clearAllFiltersClick,
  radioButtonClick,
  switchButtonClick,
  checkboxClick,
  tabClick,
  linkClick,
  buttonClick,
];
